import { Component, ComponentInterface, Element, h, Prop } from "@stencil/core";
import { ProgressAnimation } from "../icons";

export type ButtonVariant = "content" | "content-primary" | "footer" | "footer-primary";

export type ButtonSize = "icon" | "icon-stretched" | "default";

/**
 * @slot - Content is placed inside the button if provided without a slot name
 * @slot icon - Should be used to place a custom icon
 */
@Component({
    tag: "uic-button",
    styleUrl: "button.scss"
})
export class Button implements ComponentInterface {
    @Element() host: HTMLUicButtonElement;

    /**
     * The name of the icon to show next to the label.
     */
    @Prop()
    readonly icon?: string;

    /**
     * The text shown inside the button.
     * Please prefer the use of the default slot (content within the button element).
     * Becasue of a bug (https://github.com/ionic-team/stencil/issues/2054),
     * this text may not be visible when nesting button within other elements even if there is not default slot for the button.
     */
    @Prop() readonly label?: string;

    /**
     * The color variant to use. You can choose between color schemes that are suited for content or footer areas.
     * Use the `primary` variants to show visually more pronounced versions of these schemes.
     */
    @Prop({ reflect: true }) readonly variant: ButtonVariant = "content";

    /**
     * If set to `true`, the user cannot interact with the button.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * If set to `true`, the button is considered selected or toggled.
     */
    @Prop({ reflect: true }) readonly selected: boolean = false;

    /**
     * The size of the button.
     * Use `icon` for a button with just an icon.
     */
    @Prop({ reflect: true }) readonly size: ButtonSize = "default";

    /**
     * If set to `true`, the button is currently loading.
     */
    @Prop({ reflect: true }) readonly loading: boolean = false;

    /**
     * If a value is set, an <a> tag with the specified href will be rendered instead of a button.
     */
    @Prop()
    readonly href?: string;

    /**
     * If the `href` value is set, this value will be used for target attribute of the <a> tag.
     */
    @Prop()
    readonly target?: string;

    /**
     * If the `href` value is set, this value will be used for download attribute of the <a> tag.
     */
    @Prop()
    readonly download?: string;

    private get tabIndex() {
        if (this.disabled) {
            return -1;
        }
        const hasTabIndex = this.host.hasAttribute("tabindex");

        if (hasTabIndex) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return this.host.getAttribute("tabindex") as any;
        }

        return 0;
    }

    render() {
        const Proxy = this.href ? "a" : "button";
        const props = this.href
            ? { href: this.href, target: this.target, download: this.download }
            : { tabIndex: this.tabIndex, disabled: this.disabled };
        const hasCustomIcon = !!this.host.querySelector("[slot='icon']");

        return (
            <Proxy
                class={{
                    "uic-button__native-button": true,
                    selected: this.selected,
                    loading: this.loading
                }}
                {...props}
            >
                <span
                    class={{
                        "uic-button__icon": true,
                        "uic-button__icon--custom": hasCustomIcon
                    }}
                >
                    <slot name='icon'>{this.icon && <uic-icon name={this.icon}></uic-icon>}</slot>
                </span>
                <span class='uic-button__label'>
                    <slot>{this.label}</slot>
                </span>
                {this.loading && <span class='uic-button__loading'>{ProgressAnimation()}</span>}
            </Proxy>
        );
    }
}
